import { useEffect, useState } from 'react'
import ProgressionService from '../services/api/ProgressionService'

// Stable default so an absent resourceIds does not change identity each render
// (which would otherwise retrigger the effect endlessly).
const EMPTY_RESOURCE_IDS = []

const INITIAL = { grade: null, satisfaction: null, controlsCount: null, alertsCount: null }

const extractValue = payload => {
  if (!payload || Array.isArray(payload))
    return null
  const value = payload.value ?? payload.count
  return Number.isFinite(value) ? value : null
}

// Total controls = standard controls + additional controls (separate measure).
const totalControls = (controls, additional) => {
  const base = extractValue(controls)
  if (base === null)
    return null
  return base + (extractValue(additional) || 0)
}

// Fetches the four KPI metrics (note, satisfaction, controls, alerts) at the
// "total" granularity. Shared by the Statistics and Evolution metric rows.
const useKpiMeasures = (collectId, draftId, params) => {
  const [values, setValues] = useState(INITIAL)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const {
    startDate, endDate, operatorId,
    resourceIds = EMPTY_RESOURCE_IDS, resourceSearch, variableId
  } = params

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    const measureParams = {
      startDate, endDate, granularity: 'total', operatorId, resourceIds, resourceSearch, variableId
    }
    const measureFor = name =>
      ProgressionService.fetchMeasure(collectId, draftId, { name, ...measureParams })
    Promise.all([
      measureFor('grade'),
      measureFor('satisfaction'),
      measureFor('controls'),
      measureFor('additional_controls'),
      measureFor('alerts')
    ])
      .then(([grade, satisfaction, controls, additional, alerts]) => {
        if (cancelled)
          return
        setValues({
          grade: extractValue(grade),
          satisfaction: extractValue(satisfaction),
          controlsCount: totalControls(controls, additional),
          alertsCount: extractValue(alerts)
        })
        setError(false)
      })
      .catch(() => { if (!cancelled) {
        setValues(INITIAL)
        setError(true)
      } })
      .finally(() => { if (!cancelled)
        setLoading(false) })
    return () => { cancelled = true }
  }, [collectId, draftId, startDate, endDate, operatorId, resourceIds, resourceSearch, variableId])

  return { values, loading, error }
}

export default useKpiMeasures
